import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './Projects.css';

const projects = [
  {
    title: 'Thewria.com',
    image: 'https://i.imgur.com/o1xdKQR.png',
    link: 'https://github.com/justsubway/thewria-greek-sat-math',
    demo: 'https://thewria.com',
    stack: 'Node.js · JavaScript',
    description:
      'Educational platform for Greek Panhellenic exams — TikTok-style scrollable study modules used by active students.',
  },
  {
    title: 'Jarvis',
    image: 'https://opengraph.githubassets.com/1/justsubway/Jarvis',
    link: 'https://github.com/justsubway/Jarvis',
    stack: 'TypeScript · Electron · OpenAI',
    description:
      'Voice-first macOS AI desktop assistant: HUD overlay, offline wake word, 15 local tools, and long-term memory.',
  },
  {
    title: 'GeoRythm',
    image: 'https://i.imgur.com/TWsaINJ.png',
    link: 'https://github.com/justsubway/georythm',
    demo: 'https://justsubway.github.io/georythm/',
    stack: 'React · Redux · RapidAPI',
    description:
      'Music discovery app with live charts, search, and a polished player UI — real-time API data with mock fallback.',
  },
  {
    title: 'Pathly',
    image: 'https://opengraph.githubassets.com/1/justsubway/pathly-walk-safely',
    link: 'https://github.com/justsubway/pathly-walk-safely',
    stack: 'React Native · Flask · Maps',
    description:
      'Walk-safety app for Athens: AI-scored routes, bilingual UI, and a Flask backend for safety scoring.',
  },
  {
    title: 'SuperChat',
    image: 'https://i.imgur.com/VgZfwTI.png',
    link: 'https://github.com/justsubway/discord-clone-react',
    stack: 'React · Firebase',
    description:
      'Discord-style realtime chat with Google auth, guest mode, channels, and persistent Firestore messages.',
  },
  {
    title: 'Chomp',
    image: 'https://i.imgur.com/9jHtQDp.png',
    link: 'https://github.com/justsubway/food-delivery-app',
    stack: 'React Native · Expo · Appwrite',
    description:
      'Food delivery mobile app with auth, search, cart, and a refined gradient UI on Expo + Appwrite.',
  },
  {
    title: 'Resumind',
    image: 'https://i.imgur.com/nI1HcQJ.png',
    link: 'https://github.com/justsubway/ai-resume-analyzer',
    stack: 'TypeScript · AI APIs',
    description:
      'AI resume analyzer that reviews applications and returns actionable, role-specific feedback.',
  },
  {
    title: 'InstaWrapped',
    image: 'https://opengraph.githubassets.com/1/justsubway/InstaWrapped',
    link: 'https://github.com/justsubway/InstaWrapped',
    stack: 'Next.js · TypeScript',
    description:
      'Private Instagram Wrapped from your exported chats — stats slides and video export, all local.',
  },
  {
    title: 'Real Estate Listings',
    image: 'https://opengraph.githubassets.com/1/justsubway/real-estate-listings',
    link: 'https://github.com/justsubway/real-estate-listings',
    demo: 'https://justsubway.github.io/real-estate-listings/',
    stack: 'TypeScript · React · Vite',
    description:
      'Property listings frontend with a clean browse UI, deployed to GitHub Pages.',
  },
  {
    title: 'AI Code Assistant',
    image: 'https://opengraph.githubassets.com/1/justsubway/ai-code-assistant',
    link: 'https://github.com/justsubway/ai-code-assistant',
    stack: 'React · TypeScript · Vite',
    description:
      'Web app for AI-assisted coding help — interactive chat UI built with React and TypeScript.',
  },
];

const Projects = () => {
  const [hoveredProject, setHoveredProject] = useState(null);
  const [isHovering, setIsHovering] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [previousProjectIndex, setPreviousProjectIndex] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [selectedProject, setSelectedProject] = useState(null);
  const previewRef = useRef(null);

  const displayedProjects = showAll ? projects : projects.slice(0, 6);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleProjectHover = (project) => {
    if (!isMobile) {
      const currentIndex = hoveredProject
        ? projects.findIndex((p) => p.title === hoveredProject.title)
        : -1;
      setPreviousProjectIndex(currentIndex);
      setHoveredProject(project);
      setIsHovering(true);
    }
  };

  const handleProjectLeave = () => {
    if (!isMobile) {
      setTimeout(() => {
        const hoveredElement = document.querySelector('.project-title-item:hover');
        if (!hoveredElement) {
          setIsHovering(false);
          setHoveredProject(null);
          setPreviousProjectIndex(null);
        }
      }, 100);
    }
  };

  const handleProjectTap = (project) => {
    if (isMobile) {
      setSelectedProject(
        selectedProject?.title === project.title ? null : project
      );
    }
  };

  const getCurrentProjectIndex = () => {
    if (!hoveredProject) return -1;
    return projects.findIndex((p) => p.title === hoveredProject.title);
  };

  const getSlideDirection = () => {
    const currentIndex = getCurrentProjectIndex();
    if (
      previousProjectIndex === null ||
      currentIndex === -1 ||
      previousProjectIndex === -1
    ) {
      return 0;
    }
    return currentIndex > previousProjectIndex ? 1 : -1;
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' },
    },
  };

  return (
    <div className="projects-section" id="projects">
      <div className="projects-container">
        <motion.div
          className="projects-content"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ amount: 0.2, once: false }}
        >
          <motion.h2 className="projects-title" variants={itemVariants}>
            Featured Projects
          </motion.h2>
          <motion.p className="projects-subtitle" variants={itemVariants}>
            Selected work from GitHub — hover for a preview, or open the repo / live demo.
          </motion.p>

          <motion.div
            className={`projects-list ${isMobile ? 'mobile-layout' : ''}`}
            variants={itemVariants}
            onMouseLeave={() => {
              setTimeout(() => {
                setIsHovering(false);
                setHoveredProject(null);
                setPreviousProjectIndex(null);
              }, 200);
            }}
          >
            {displayedProjects.map((project) => (
              <motion.div
                key={project.title}
                className={`project-item ${
                  isMobile ? 'mobile-project-item' : 'project-title-item'
                } ${selectedProject?.title === project.title ? 'selected' : ''}`}
                onMouseEnter={() => handleProjectHover(project)}
                onMouseLeave={handleProjectLeave}
                onClick={() => handleProjectTap(project)}
                whileHover={!isMobile ? { scale: 1.02 } : {}}
                whileTap={isMobile ? { scale: 0.98 } : {}}
                transition={{ duration: 0.2 }}
              >
                {isMobile ? (
                  <div className="mobile-project-content">
                    <div className="mobile-project-header">
                      <h3 className="project-title">{project.title}</h3>
                      <div className="mobile-project-arrow">
                        {selectedProject?.title === project.title ? '−' : '+'}
                      </div>
                    </div>
                    <AnimatePresence>
                      {selectedProject?.title === project.title && (
                        <motion.div
                          className="mobile-project-details"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: 'easeInOut' }}
                        >
                          <div className="mobile-project-image">
                            <img
                              src={project.image}
                              alt={project.title}
                              className="project-image"
                            />
                          </div>
                          <div className="mobile-project-info">
                            <p className="project-stack">{project.stack}</p>
                            <p className="project-description">
                              {project.description}
                            </p>
                            <div className="project-link-row">
                              <a
                                href={project.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="project-link-button"
                              >
                                GitHub →
                              </a>
                              {project.demo && (
                                <a
                                  href={project.demo}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="project-link-button project-link-demo"
                                >
                                  Live Demo →
                                </a>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <h3 className="project-title">
                    <a
                      href={project.demo || project.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="project-link"
                    >
                      {project.title}
                    </a>
                  </h3>
                )}
              </motion.div>
            ))}
          </motion.div>

          {!showAll && (
            <motion.button
              className="show-all-button"
              onClick={() => setShowAll(true)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              variants={itemVariants}
            >
              Show All Projects
            </motion.button>
          )}
        </motion.div>

        <AnimatePresence>
          {isHovering && hoveredProject && !isMobile && (
            <motion.div
              ref={previewRef}
              className="project-preview-container"
              style={{
                left: mousePosition.x,
                top: mousePosition.y,
              }}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
            >
              <div className="project-preview-content">
                <motion.div
                  className="project-preview-image-container"
                  key={hoveredProject.title}
                  initial={{
                    y: getSlideDirection() * 100,
                    opacity: 0,
                  }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{
                    y: getSlideDirection() * -100,
                    opacity: 0,
                  }}
                  transition={{ duration: 0.4, ease: 'easeOut' }}
                  onAnimationComplete={() => {
                    setPreviousProjectIndex(getCurrentProjectIndex());
                  }}
                >
                  <img
                    src={hoveredProject.image}
                    alt={hoveredProject.title}
                    className="project-preview-image"
                  />
                </motion.div>

                <motion.div
                  className="project-preview-info"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1, duration: 0.3 }}
                >
                  <h4 className="project-preview-title">
                    {hoveredProject.title}
                  </h4>
                  <p className="project-preview-stack">{hoveredProject.stack}</p>
                  <p className="project-preview-description">
                    {hoveredProject.description}
                  </p>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Projects;
